import { memo, useCallback, useContext, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Zap } from 'react-feather';
import styled from '@emotion/styled';
import Sparkle from '@mui/icons-material/AutoAwesomeOutlined';
import { createId } from '@paralleldrive/cuid2';
import type { BrandInsightsInstanceContext, ChatAction } from '@trinity/chat';
import { ChatActionType, ChatContextType, KolSearchStatusEnum } from '@trinity/chat';
import { useSetChatInputText } from '@trinity/chat-components/components/Chat/hooks/useSetChatInputText';
import { useSubmitQueryMutation } from '@trinity/chat-components/components/Chat/hooks/useSubmitQueryMutation';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { buildDefaultChatContext } from '@trinity/chat-components/components/Chat.State/state.initial-state';
import { LandingCardIconRenderer } from '@trinity/chat-components/components/LandingCardIcon/LandingCardIconRenderer.js';
import { useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import {
  agentResponseJsonDataSchema,
  chatActionActionDataSchema,
  hcpSearchActionDataSchema,
  LandingCardActionTypeEnum,
} from '@trinity/management-app-client';
import type { ChatState } from '../Chat.State/interfaces.chat';
import {
  ActivePageContext,
  AuthorContext,
  ChatContextItemsContext,
  ChatLandingCardsContext,
  ChatLandingCardsPromptsContext,
  ChatLandingCardsUseNewExperienceContext,
} from './context';
import type { LandingCard } from './interfaces.context';

interface LandingCardProps {
  title: string | null;
  description: string | null;
  icon: string | null;
  onClick: () => void;
}

interface PromptInjectionActionData {
  prompt: string;
}

interface RedirectActionData {
  url: string;
}

interface DispatchActionData {
  contextOverride?: BrandInsightsInstanceContext.ItemWithMetadata;
}

export const LandingCards = memo(() => {
  const dispatch = useContext(ChatStateDispatchContext);
  const brandIdentifier = useActiveBrandIdentifier();
  const navigate = useNavigate();
  const submitQueryMutation = useSubmitQueryMutation();
  const chatContexts = useContext(ChatContextItemsContext);
  const useNewExperience = useContext(ChatLandingCardsUseNewExperienceContext);
  const { setPage } = useContext(ActivePageContext);

  const setChatInputText = useSetChatInputText();
  const cards = useContext(ChatLandingCardsContext);
  const prompts = useContext(ChatLandingCardsPromptsContext);

  const dispatchActions = useCallback(
    (actions: ChatState.BaseAction[]) => {
      dispatch({
        type: 'batch-actions',
        payload: {
          actions: [
            ...actions,
            {
              type: 'landing-cards-visibility-toggled',
              payload: { visible: false },
            },
          ],
        },
      });
    },
    [dispatch]
  );

  const handleDispatchAction = useCallback(
    (card: LandingCard) => {
      return () => {
        if (!card.chatContext || !chatContexts) return;
        if (!brandIdentifier) {
          throw new Error('Brand identifier is required for Agent Response action');
        }

        const actionData = card.actionData as DispatchActionData;

        const fullContext = chatContexts.find((ctx) => ctx.identifier === card.chatContext?.identifier);
        if (!fullContext) return;

        // Use the same logic as Chat.Agents.Container
        const profileAgent = fullContext.agent.identifier === 'kol-profile';
        const globalAgent = fullContext.agent.identifier === 'global';

        let context: BrandInsightsInstanceContext.ItemWithMetadata = {
          type: ChatContextType.Agent,
          data: fullContext,
        };
        if (actionData?.contextOverride) {
          context = actionData.contextOverride;
        } else if (profileAgent) {
          context = buildDefaultChatContext(ChatContextType.KolProfile);
        } else if (globalAgent) {
          context = buildDefaultChatContext(ChatContextType.Global);
        }

        dispatchActions([
          {
            type: 'instance-context-changed',
            payload: {
              context,
              brandIdentifier,
            },
          },
          {
            type: 'default-context-updated',
            payload: {
              defaultContext: context,
            },
          },
        ]);

        setPage(fullContext.pageId ?? null, fullContext.layout);
      };
    },
    [dispatchActions, brandIdentifier, chatContexts, setPage]
  );

  const handlePromptInjectionAction = useCallback(
    (card: LandingCard) => {
      return () => {
        if (!card.chatContext || !chatContexts) return;

        if (!brandIdentifier) {
          throw new Error('Brand identifier is required for Agent Response action');
        }

        const fullContext = chatContexts.find((ctx) => ctx.identifier === card.chatContext?.identifier);
        if (!fullContext) return;

        const profileAgent = fullContext.agent.identifier === 'kol-profile';
        const globalAgent = fullContext.agent.identifier === 'global';

        let context: BrandInsightsInstanceContext.ItemWithMetadata;
        if (profileAgent) {
          context = buildDefaultChatContext(ChatContextType.KolProfile);
        } else if (globalAgent) {
          context = buildDefaultChatContext(ChatContextType.Global);
        } else {
          context = {
            type: ChatContextType.Agent,
            data: fullContext,
          };
        }

        dispatchActions([
          {
            type: 'instance-context-changed',
            payload: {
              context,
              brandIdentifier,
            },
          },
          {
            type: 'default-context-updated',
            payload: {
              defaultContext: context,
            },
          },
        ]);

        setPage(fullContext.pageId ?? null, fullContext.layout);

        const promptData = card.actionData as PromptInjectionActionData;
        if (promptData?.prompt) {
          const queryIdentifier = createId();

          submitQueryMutation.mutate({
            identifier: queryIdentifier,
            parentQueryIdentifier: null,
            query: promptData.prompt,
            action: null,
            context: context,
          });
        }
      };
    },
    [dispatchActions, brandIdentifier, chatContexts, setPage, submitQueryMutation]
  );

  const handleQuestionBankClick = useCallback(() => {
    dispatchActions([
      {
        type: 'kol-search-added',
        payload: {
          item: {
            id: null,
            createdOn: new Date(),
            status: KolSearchStatusEnum.QuestionsBank,
            identifier: createId(),
            followupQuestions: [],
            form: {
              name: '',
            },
            results: null,
            profile: null,
            recommendations: {
              status: 'skipped',
              items: [],
              total: 0,
            },
            citations: [],
          },
        },
      },
    ]);
  }, [dispatchActions]);

  const handleSearchClick = useCallback(() => {
    dispatchActions([
      {
        type: 'kol-search-added',
        payload: {
          item: {
            id: null,
            createdOn: new Date(),
            status: KolSearchStatusEnum.FormEntry,
            identifier: createId(),
            followupQuestions: [],
            form: {
              name: '',
            },
            results: null,
            profile: null,
            recommendations: {
              status: 'skipped',
              items: [],
              total: 0,
            },
            citations: [],
          },
        },
      },
    ]);
  }, [dispatchActions]);

  const handleRecommendationsClick = useCallback(() => {
    dispatchActions([
      {
        type: 'kol-search-added',
        payload: {
          item: {
            id: null,
            createdOn: new Date(),
            status: KolSearchStatusEnum.ProfileRecommendations,
            identifier: createId(),
            followupQuestions: [],
            form: {
              name: '',
            },
            results: null,
            profile: null,
            recommendations: {
              status: 'uninitialized',
              items: [],
              total: 0,
            },
            citations: [],
          },
        },
      },
    ]);
  }, [dispatchActions]);

  type CustomActionData = {
    function: string;
  };

  const handleCustomAction = useCallback(
    (card: LandingCard) => {
      return () => {
        const customActionData = card.actionData as CustomActionData;
        switch (customActionData.function) {
          case '2':
            handleQuestionBankClick();
            break;
          case '3':
            handleRecommendationsClick();
            break;
          case '4':
            handleSearchClick();
            break;
          default:
        }
      };
    },
    [handleQuestionBankClick, handleRecommendationsClick, handleSearchClick]
  );

  const handleRedirectAction = useCallback(
    (card: LandingCard) => {
      return () => {
        const redirectData = card.actionData as RedirectActionData;
        //handle external vs internal links
        if (redirectData?.url) {
          if (redirectData.url.startsWith('http://') || redirectData.url.startsWith('https://')) {
            window.location.href = redirectData.url;
          } else {
            navigate(redirectData.url);
          }
        }
      };
    },
    [navigate]
  );

  const handleAgentResponseAction = useCallback(
    (card: LandingCard) => {
      return () => {
        if (!chatContexts) return;

        const agentResponseData = card.actionData;
        if (!agentResponseData) return;

        if (!brandIdentifier) {
          throw new Error('Brand identifier is required for Agent Response action');
        }

        // Parse and validate the actionData using the shared schema
        const parseResult = agentResponseJsonDataSchema.safeParse(agentResponseData);

        if (!parseResult.success) {
          return;
        }

        const { subject } = parseResult.data;

        const subjectId = Number(subject.id);

        const currentContext = chatContexts[0];
        if (!currentContext) return;

        const contextWithSubject: BrandInsightsInstanceContext.ItemWithMetadata = {
          type: ChatContextType.Agent,
          data: {
            ...currentContext,
            subject: {
              id: subjectId,
              name: subject.name,
            },
          },
        };

        // Set the context without submitting a query
        dispatch({
          type: 'batch-actions',
          payload: {
            actions: [
              {
                type: 'instance-context-changed',
                payload: {
                  context: contextWithSubject,
                  brandIdentifier,
                },
              },
              {
                type: 'default-context-updated',
                payload: {
                  defaultContext: contextWithSubject,
                },
              },
            ],
          },
        });

        setPage(currentContext.pageId ?? null, currentContext.layout);
      };
    },
    [chatContexts, dispatch, brandIdentifier, setPage]
  );

  const handleChatActionAction = useCallback(
    (card: LandingCard) => {
      return () => {
        if (!card.chatContext) return;
        if (!chatContexts) return;

        if (!brandIdentifier) {
          throw new Error('Brand identifier is required for Agent Response action');
        }

        const parseResult = chatActionActionDataSchema.safeParse(card.actionData);
        if (!parseResult.success) return;

        const actionData = parseResult.data;

        const fullContext = chatContexts.find((ctx) => ctx.identifier === card.chatContext?.identifier);
        if (!fullContext) return;

        const context: BrandInsightsInstanceContext.ItemWithMetadata = {
          type: ChatContextType.Agent,
          data: fullContext,
        };

        dispatchActions([
          {
            type: 'instance-context-changed',
            payload: {
              context,
              brandIdentifier,
            },
          },
          {
            type: 'default-context-updated',
            payload: {
              defaultContext: context,
            },
          },
        ]);

        setPage(fullContext.pageId ?? null, fullContext.layout);

        const queryIdentifier = createId();

        const action: ChatAction =
          actionData.chatActionType === 'InitiateFormWorkflow'
            ? {
                type: ChatActionType.InitiateFormWorkflow,
                payload: { workflowKey: actionData.workflowKey },
              }
            : {
                type: ChatActionType.StartFrmAccountSearch,
              };

        submitQueryMutation.mutate({
          identifier: queryIdentifier,
          parentQueryIdentifier: null,
          query: actionData.prompt,
          action,
          context: context,
        });
      };
    },
    [dispatchActions, brandIdentifier, chatContexts, setPage, submitQueryMutation]
  );

  const handleHcpSearchAction = useCallback(
    (card: LandingCard) => {
      return () => {
        if (!card.chatContext) return;
        if (!chatContexts) return;

        if (!brandIdentifier) {
          throw new Error('Brand identifier is required for HCP Search action');
        }

        const parseResult = hcpSearchActionDataSchema.safeParse(card.actionData ?? {});
        if (!parseResult.success) return;

        const fullContext = chatContexts.find((ctx) => ctx.identifier === card.chatContext?.identifier);
        if (!fullContext) return;

        const context: BrandInsightsInstanceContext.ItemWithMetadata = {
          type: ChatContextType.Agent,
          data: fullContext,
        };

        dispatchActions([
          {
            type: 'instance-context-changed',
            payload: {
              context,
              brandIdentifier,
            },
          },
          {
            type: 'default-context-updated',
            payload: {
              defaultContext: context,
            },
          },
        ]);

        setPage(fullContext.pageId ?? null, fullContext.layout);

        const queryIdentifier = createId();

        submitQueryMutation.mutate({
          identifier: queryIdentifier,
          parentQueryIdentifier: null,
          query: 'Open HCP search form.',
          action: {
            type: ChatActionType.StartHcpSearch,
          },
          context,
        });
      };
    },
    [dispatchActions, brandIdentifier, chatContexts, setPage, submitQueryMutation]
  );

  const cardProps = useMemo(() => {
    if (!cards || !Array.isArray(cards)) return [];

    return cards
      .sort((a, b) => a.order - b.order)
      .map((card): LandingCardProps => {
        let onClick: () => void;

        switch (card.actionType) {
          case LandingCardActionTypeEnum.Dispatch:
            onClick = handleDispatchAction(card);
            break;
          case LandingCardActionTypeEnum.PromptInjection:
            onClick = handlePromptInjectionAction(card);
            break;
          case LandingCardActionTypeEnum.Redirect:
            onClick = handleRedirectAction(card);
            break;
          case LandingCardActionTypeEnum.PassResponseDirectlyToAgent:
            onClick = handleAgentResponseAction(card);
            break;
          case LandingCardActionTypeEnum.ChatAction:
            onClick = handleChatActionAction(card);
            break;
          case LandingCardActionTypeEnum.HcpSearch:
            onClick = handleHcpSearchAction(card);
            break;
          default:
            onClick = handleCustomAction(card);
            break;
        }

        return {
          title: card.title,
          description: card.description,
          icon: card.icon,
          onClick,
        };
      });
  }, [
    cards,
    handleDispatchAction,
    handlePromptInjectionAction,
    handleRedirectAction,
    handleAgentResponseAction,
    handleChatActionAction,
    handleHcpSearchAction,
    handleCustomAction,
  ]);

  const author = useContext(AuthorContext);

  const ITEMS_PER_PAGE = 4;
  const PROMPT_ITEM_WIDTH = 195;
  const PROMPT_GAP = 10;
  const totalPromptPages = Math.ceil((prompts?.length ?? 0) / ITEMS_PER_PAGE);
  const hasPromptPagination = totalPromptPages > 1;
  const [promptPage, setPromptPage] = useState(0);
  const currentPromptPage = Math.min(promptPage, Math.max(totalPromptPages - 1, 0));

  if (!useNewExperience) {
    return (
      <Root cardCount={cardProps.length}>
        {cardProps.map((card, index) => (
          <LegacyCardComponent
            key={index}
            title={card.title}
            description={card.description}
            icon={card.icon}
            onClick={card.onClick}
          />
        ))}
      </Root>
    );
  }

  return (
    <div>
      <Greeting>
        <GreetingHeader>{`Good to see you again, ${author?.profile?.firstName}`}</GreetingHeader>
        <GreetingSubheader>{`Jump in by choosing an action below or try a prompt suggestion to get started.`}</GreetingSubheader>
      </Greeting>

      <Section>
        <SectionHeader>
          <SectionIcon>
            <Zap size={16} />
          </SectionIcon>
          <SectionTitle>Quick Actions</SectionTitle>
        </SectionHeader>

        <Actions>
          <Cards cardCount={cardProps.length}>
            {cardProps.map((card, index) => (
              <LandingCardComponent
                key={index}
                title={card.title}
                description={card.description}
                icon={card.icon}
                onClick={card.onClick}
              />
            ))}
          </Cards>
        </Actions>
      </Section>

      {prompts?.length > 0 && (
        <Section>
          <SectionHeader>
            <SectionIcon>
              <Sparkle style={{ width: 16, height: 16 }} width={16} height={16} />
            </SectionIcon>
            <SectionTitle>Prompt Suggestions</SectionTitle>
            {hasPromptPagination && (
              <CarouselControls>
                <CarouselArrow
                  onClick={() => setPromptPage((p) => Math.max(p - 1, 0))}
                  disabled={currentPromptPage === 0}
                >
                  <ChevronLeft size={20} />
                </CarouselArrow>
                <CarouselPageIndicator>{`${currentPromptPage + 1}/${totalPromptPages}`}</CarouselPageIndicator>
                <CarouselArrow
                  onClick={() => setPromptPage((p) => Math.min(p + 1, totalPromptPages - 1))}
                  disabled={currentPromptPage >= totalPromptPages - 1}
                >
                  <ChevronRight size={20} />
                </CarouselArrow>
              </CarouselControls>
            )}
          </SectionHeader>
          <PromptsViewport>
            <PromptsTrack offset={currentPromptPage * ITEMS_PER_PAGE * (PROMPT_ITEM_WIDTH + PROMPT_GAP)}>
              {prompts.map((x, index) => (
                <LandingCardPromptComponent
                  key={`${x.name}-${index}`}
                  name={x.name}
                  description={x.description}
                  onClick={() =>
                    setChatInputText(x.name, {
                      promptHintIdentifier: x.promptIdentifier ?? null,
                    })
                  }
                />
              ))}
            </PromptsTrack>
          </PromptsViewport>
        </Section>
      )}
    </div>
  );
});

LandingCards.displayName = 'LandingCards';

const LandingCardComponent = memo(({ title, description, icon, onClick }: LandingCardProps) => {
  return (
    <Card onClick={onClick}>
      <Wrap>
        <Main>
          <Graphic>
            {icon && (
              <LandingCardIconRenderer
                icon={icon}
                alt={title || 'Card icon'}
                size={30}
                width={30}
                height={30}
              />
            )}
          </Graphic>
          <Info>
            <Title>{title}</Title>
            <Description>{description}</Description>
          </Info>
        </Main>
      </Wrap>
    </Card>
  );
});

LandingCardComponent.displayName = 'LandingCardComponent';

const LandingCardPromptComponent = memo(
  ({ name, description, onClick }: { name: string; description: string; onClick: () => void }) => {
    const ariaLabel = description.trim().length > 0 ? `${name}. ${description}` : name;

    return (
      <PromptItem
        role="button"
        tabIndex={0}
        title={description}
        aria-label={ariaLabel}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
      >
        <PromptWrap>
          <PromptText>{name}</PromptText>
        </PromptWrap>
      </PromptItem>
    );
  }
);

const Actions = styled.div({
  display: 'flex',
  justifyContent: 'center',
});

const Section = styled.div({
  maxWidth: 815,
  margin: '0 auto 30px',
});

const Cards = styled.div<{
  cardCount: number;
}>(({ cardCount }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
  justifyContent: 'flex-start',
  alignContent: 'center',
  maxWidth: 815,
}));

const Card = styled.div({
  width: 400,
  height: 100,
  backgroundColor: 'var(--pri-01)',
  borderRadius: 8,
  border: '1px solid #E5E7EB',

  '&:hover': {
    boxShadow: '0px 0px 12px 0px #0000001A',
    cursor: 'pointer',
  },
});

const Wrap = styled.div({
  padding: 15,
  display: 'flex',
  alignItems: 'center',
});

const Main = styled.div({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: 15,
});

const Graphic = styled.div({
  width: 40,
  height: 40,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
});

const Info = styled.div(({ theme }) => ({}));

const Title = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 14,
  marginBottom: 10,
}));

const Description = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.regular,
  fontSize: 14,
  color: theme.palette.gray.main,
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

const Greeting = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  margin: '20px 0',
});

const GreetingHeader = styled.div(({ theme }) => ({
  fontSize: 24,
  fontFamily: theme.fonts.semiBold,
}));

const GreetingSubheader = styled.div(({ theme }) => ({
  fontSize: 16,
  fontFamily: theme.fonts.regular,
  color: '#374151',
}));

const SectionHeader = styled.div({
  display: 'flex',
  gap: 5,
  alignItems: 'center',
  color: '#374151',
  padding: '10px 0',
});

const SectionIcon = styled.div({
  color: '#374151',
});

const SectionTitle = styled.div({
  fontSize: 13,
  color: '#374151',
  textTransform: 'uppercase',
  fontFamily: 'var(--font-reg)',
});

const PromptsViewport = styled.div({
  overflow: 'hidden',
});

const PromptsTrack = styled.div<{ offset: number }>(({ offset }) => ({
  display: 'flex',
  gap: 10,
  transform: `translateX(-${offset}px)`,
  transition: 'transform 0.3s ease',
}));

const CarouselControls = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  marginLeft: 'auto',
});

const CarouselPageIndicator = styled.span(({ theme }) => ({
  fontSize: 14,
  fontFamily: theme.fonts.regular,
  color: '#374151',
  minWidth: 28,
  textAlign: 'center',
}));

const CarouselArrow = styled.button(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: 4,
  backgroundColor: 'transparent',
  cursor: 'pointer',
  color: '#374151',
  padding: 0,

  '&:disabled': {
    opacity: 0.35,
    cursor: 'not-allowed',
  },
}));

const PromptItem = styled.div({
  width: 195,
  flexShrink: 0,
  height: 100,
  backgroundColor: 'var(--pri-01)',
  borderRadius: 8,
  border: '1px solid #E5E7EB',

  '&:hover': {
    boxShadow: '0px 0px 12px 0px #0000001A',
    cursor: 'pointer',
  },
});

const PromptWrap = styled.div({
  padding: 15,
});

const PromptText = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.regular,
  fontSize: 14,
  display: '-webkit-box',
  color: theme.palette.black.light1,
  WebkitLineClamp: 3,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
}));

const LegacyCardComponent = memo(({ title, description, icon, onClick }: LandingCardProps) => {
  return (
    <CardRoot
      role="button"
      tabIndex={0}
      aria-label={title?.trim() ? title : 'Landing card'}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <CardHeader>
        {icon && <LandingCardIconRenderer icon={icon} alt={title || 'Card icon'} width={40} height={40} />}
      </CardHeader>

      <CardTitle>{title}</CardTitle>

      <CardCopy>{description}</CardCopy>

      <CardFooter>
        <CardLink>
          <StyledChevronRightIcon size={20} />
        </CardLink>
      </CardFooter>
    </CardRoot>
  );
});

LegacyCardComponent.displayName = 'LegacyCardComponent';

const CARD_WIDTH = 258;
const GAP = 15;

const Root = styled.div<{ cardCount: number }>(({ cardCount }) => {
  const columns = cardCount === 4 ? 2 : 3;
  return {
    display: 'flex',
    flexWrap: 'wrap',
    gap: GAP,
    justifyContent: 'center',
    alignContent: 'center',
    margin: '0 auto',
    flex: 1,
    width: '100%',
    maxWidth: columns * CARD_WIDTH + (columns - 1) * GAP,
    containerType: 'inline-size',
  };
});

const CardLink = styled.div(() => ({
  borderRadius: '50%',
  padding: 4,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
}));

const StyledChevronRightIcon = styled(ChevronRight)(({ theme }) => ({
  color: theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main,
  paddingLeft: 2,
  strokeWidth: 3,
  transition: 'transform 0.3s',
}));

const CardRoot = styled.div(({ theme }) => ({
  padding: 15,
  borderRadius: 8,
  backgroundColor: theme.palette.white.main,
  border: `2px solid ${theme.palette.gray.light4}`,
  cursor: 'pointer',

  width: 258,
  height: 280,

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'space-between',

  '@container (max-width: 530px)': {
    height: 120,
    padding: 10,
  },

  '&:hover': {
    backgroundColor: theme.isTrinityThemed
      ? theme.palette.sentiment.primary.light
      : theme.palette.btnPrimary.bg,
    border: `2px solid ${
      theme.isTrinityThemed ? theme.palette.sentiment.primary.main : theme.palette.primary.main
    }`,
  },

  [`&:hover ${CardLink}`]: {
    backgroundColor: theme.isTrinityThemed
      ? theme.palette.sentiment.primary.main
      : theme.palette.primary.main,
  },

  [`&:hover ${StyledChevronRightIcon}`]: {
    transform: 'rotate(90deg)',
    color: theme?.isTrinityThemed ? theme.palette.white.main : theme.palette?.wl?.primary?.text,
  },
}));

const CardHeader = styled.div(() => ({
  display: 'flex',
  justifyContent: 'flex-start',
  marginBottom: 10,
  '@container (max-width: 530px)': {
    marginBottom: 5,
    '& svg, & img': {
      width: '24px !important',
      height: '24px !important',
    },
  },
}));

const CardTitle = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 18,
  marginTop: 10,
  height: 63,
  '@container (max-width: 530px)': {
    fontSize: 14,
    marginTop: 5,
    height: 'auto',
  },
}));

const CardCopy = styled.div({
  '@container (max-width: 530px)': {
    display: 'none',
  },
});

const CardFooter = styled.div({
  display: 'flex',
  justifyContent: 'center',
});
